"use client";

import React, { useState, useCallback } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Widget } from "@/modules";
import { WidgetFactoryWrapper, useWidgetData } from "@/app/widgets/WidgetFactoryContext";
import { WidgetFactoryComponent } from "@/app/widgets/WidgetFactory";
import { NoDataView } from "@/app/widgets/NoDataView";
import { ErrorView } from "@/app/widgets/ErrorView";
import { RangeDatePicker } from "@/components/molecules/DatePicker/RangeDatePicker";
import { DateRange } from "@/components/molecules/DatePicker/Preset";

interface DateOverride {
    startDate: string;
    endDate: string;
}

interface WidgetPreviewModalProps {
    widget: Widget;
    onConfirm: () => void;
    onClose: () => void;
    confirmLabel?: string;
    forceEnableConfirm?: boolean;
}

interface PreviewContentProps extends WidgetPreviewModalProps {
    onDateOverride: (range: DateRange) => void;
    dateOverride: DateOverride | null;
}

function PreviewContent({ widget, onConfirm, onClose, confirmLabel, forceEnableConfirm, onDateOverride, dateOverride }: PreviewContentProps) {
    const { loading, error, noData } = useWidgetData();
    const isConfirmDisabled = forceEnableConfirm ? loading : loading || !!error || noData;
    const hasUserDataFilters = ((widget?.metadata as Record<string, unknown> | undefined)?.["data-filters"] as unknown[] | undefined)?.length ? true : false;

    const handleDateRangeChange = useCallback((range: DateRange | undefined) => {
        if (range?.from && range?.to) {
            onDateOverride(range);
        }
    }, [onDateOverride]);

    const currentDateRange: DateRange | undefined = dateOverride
        ? { from: new Date(dateOverride.startDate + "T00:00:00"), to: new Date(dateOverride.endDate + "T00:00:00") }
        : undefined;

    const noDataDescription = hasUserDataFilters ? (
        <p className="text-gray-500 text-center text-sm" style={{ fontFamily: "Roboto, sans-serif" }}>
            The active data filters returned zero rows for this date range.
        </p>
    ) : (
        <p className="text-gray-500 text-center text-sm" style={{ fontFamily: "Roboto, sans-serif" }}>
            No data was found for the selected date range. Use the date picker in the header to try a different period.
        </p>
    );

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center justify-between border-b border-[#e0e0e0] px-6 py-3">
                <h2 className="text-xl font-semibold text-[#2d3a3f] font-roboto">Widget Preview</h2>
                <div className="flex items-center gap-3">
                    <RangeDatePicker
                        value={currentDateRange}
                        onChange={handleDateRangeChange}
                        showQuickSelect
                        trigger={
                            <button
                                type="button"
                                className="flex items-center gap-2 rounded-md border border-[#e0e0e0] bg-white px-3 py-1.5 text-sm text-[#5a717a] transition hover:bg-[#f3f5f6] hover:text-[#2d3a3f]"
                                style={{ fontFamily: "Roboto, sans-serif" }}
                            >
                                <CalendarDays className="h-4 w-4 shrink-0" />
                                <span>{dateOverride ? `${dateOverride.startDate} – ${dateOverride.endDate}` : "Dashboard date"}</span>
                            </button>
                        }
                    />
                    <button onClick={onClose} className="text-[#5a717a] transition hover:text-[#2d3a3f]" aria-label="Close preview">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 min-h-0">
                {error ? (
                    <ErrorView className="h-full" />
                ) : noData && !loading ? (
                    <NoDataView
                        className="h-full"
                        message={hasUserDataFilters ? "No matches for your filters" : "No data for this date range"}
                        description={noDataDescription}
                        action={hasUserDataFilters ? { label: "Edit filters", onClick: onClose } : undefined}
                    />
                ) : (
                    <WidgetFactoryComponent />
                )}
            </div>

            <div className="border-t border-[#e0e0e0] px-6 py-4 flex justify-end gap-3">
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border border-[#e0e0e0] px-6 py-2 text-sm font-medium text-[#2d3a3f] transition hover:bg-[#f3f5f6] font-roboto"
                >
                    Close
                </button>
                <button
                    type="button"
                    onClick={onConfirm}
                    disabled={isConfirmDisabled}
                    className={`rounded-lg px-6 py-2 text-sm font-medium text-white transition font-roboto ${
                        isConfirmDisabled ? "cursor-not-allowed bg-[#97a7ad]" : "bg-[#079a95] hover:bg-[#067a76]"
                    }`}
                >
                    {confirmLabel ?? "Create Widget"}
                </button>
            </div>
        </div>
    );
}

const noop = () => {};
const setDraggingDisabledNoop: React.Dispatch<React.SetStateAction<boolean>> = noop;

const getWidthClass = (widget: Widget): string => {
    if (widget.type === "chart" && widget.chartType === "funnel") {
        return "w-[720px]";
    }

    switch (widget.type) {
        case "ticker":
        case "budget-tickers-group-by-product":
            return "w-[480px]";
        case "iframe":
        case "data-sources":
            return "w-[70vw] max-w-[900px]";
        default:
            return "w-[80vw] max-w-[1200px]";
    }
};

const getHeightClass = (widget: Widget): string => {
    switch (widget.type) {
        case "ticker":
        case "budget-tickers-group-by-product":
            return "max-h-[90vh]";
        default:
            return "h-[85vh] max-h-[85vh]";
    }
};

export function WidgetPreviewModal({ widget, onConfirm, onClose, confirmLabel, forceEnableConfirm }: WidgetPreviewModalProps) {
    const [dateOverride, setDateOverride] = useState<DateOverride | null>(null);

    const handleDateOverride = useCallback((range: DateRange) => {
        if (range.from && range.to) {
            setDateOverride({
                startDate: format(range.from, "yyyy-MM-dd"),
                endDate: format(range.to, "yyyy-MM-dd"),
            });
        }
    }, []);

    const effectiveWidget: Widget = dateOverride
        ? ({
              ...widget,
              datePeriod: "custom",
              service: {
                  ...widget.service,
                  parameters: {
                      ...(widget.service as any)?.parameters,
                      startDate: dateOverride.startDate,
                      endDate: dateOverride.endDate,
                  },
              },
          } as unknown as Widget)
        : widget;

    const widthClass = getWidthClass(widget);
    const heightClass = getHeightClass(widget);

    return (
        <Dialog.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
            <AnimatePresence>
                <Dialog.Portal forceMount>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 z-[10000] bg-[#0f1b21]/50"
                    />
                    <Dialog.Content asChild>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className={`fixed left-1/2 top-1/2 z-[10001] flex -translate-x-1/2 -translate-y-1/2 flex-col rounded-2xl bg-white shadow-2xl focus:outline-none ${widthClass} ${heightClass}`}
                        >
                            <WidgetFactoryWrapper key={dateOverride ? `${dateOverride.startDate}-${dateOverride.endDate}` : "default"} widget={effectiveWidget} setDraggingDisabled={setDraggingDisabledNoop}>
                                <PreviewContent
                                    widget={effectiveWidget}
                                    onConfirm={onConfirm}
                                    onClose={onClose}
                                    confirmLabel={confirmLabel}
                                    forceEnableConfirm={forceEnableConfirm}
                                    onDateOverride={handleDateOverride}
                                    dateOverride={dateOverride}
                                />
                            </WidgetFactoryWrapper>
                        </motion.div>
                    </Dialog.Content>
                </Dialog.Portal>
            </AnimatePresence>
        </Dialog.Root>
    );
}
